import React, { MouseEvent, ReactNode, useCallback } from "react";

import { faStar } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tooltip, { TooltipProps } from "antd/es/tooltip";
import cn from "classnames";

import EntityType from "@mapmycustomers/shared/enum/EntityType";
import { Company, Deal, Person } from "@mapmycustomers/shared/types/entity";
import { EntityTypeShapedIcon } from "@mapmycustomers/ui";

import Chip, { ChipProps } from "@app/component/Chip";

import styles from "./EntityChip.module.scss";

type SupportedEntityIds = (Company | Deal | Person)["id"];
type SupportedEntityTypes =
  | EntityType.COMPANY
  | EntityType.COMPANY_ROUTE
  | EntityType.DEAL
  | EntityType.PEOPLE_ROUTE
  | EntityType.PERSON
  | EntityType.ROUTE;

export interface EntityChipProps extends Pick<ChipProps, "inline"> {
  className?: string;
  entityId: SupportedEntityIds;
  entityType: SupportedEntityTypes;
  name: string;
  onClick?: (
    payload: { entityId: SupportedEntityIds; entityType: SupportedEntityTypes },
    e?: MouseEvent<HTMLElement>
  ) => void;
  primary?: boolean;
  suppressTooltip?: boolean;
  tooltip?: ReactNode;
  tooltipProps?: Omit<TooltipProps, "title">;
}

const EntityChip = ({
  className,
  entityId,
  entityType,
  name,
  onClick,
  primary,
  suppressTooltip,
  tooltip,
  tooltipProps,
  ...props
}: EntityChipProps) => {
  const handleClick = useCallback(
    (e?: MouseEvent<HTMLElement>) => {
      onClick?.({ entityId, entityType }, e);
    },
    [entityId, entityType, onClick]
  );

  return (
    <Tooltip title={tooltip ?? name} trigger={suppressTooltip ? [] : undefined} {...tooltipProps}>
      <Chip
        className={cn(styles.container, className)}
        icon={
          <EntityTypeShapedIcon
            className={styles.icon}
            entityType={entityType}
            onClick={handleClick}
            shape="square"
          />
        }
        onClick={handleClick}
        {...props}
      >
        <span className={styles.text}>{name}</span>
        {primary && <FontAwesomeIcon className={styles.star} icon={faStar} />}
      </Chip>
    </Tooltip>
  );
};

export default EntityChip;
